<template>
  <div class="ai-overview-page">
    <div class="container mx-auto px-6 py-6">
      <!-- Header -->
      <div class="mb-6">
        <h1 class="text-3xl font-bold text-foreground mb-2">
          Presentation Manager
        </h1>
        <p class="text-muted-foreground mb-6">
          Manage your presentation content, timing, and settings
        </p>
        
        <!-- Quick Stats -->
        <div class="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div class="bg-card p-4 rounded-lg border">
            <div class="text-2xl font-bold text-primary">{{ slidesStore.totalSlides }}</div>
            <div class="text-sm text-muted-foreground">Total Slides</div>
          </div>
          <div class="bg-card p-4 rounded-lg border">
            <div class="text-2xl font-bold text-primary">{{ totalEditableFields }}</div>
            <div class="text-sm text-muted-foreground">Editable Fields</div>
          </div>
          <div class="bg-card p-4 rounded-lg border">
            <div class="text-2xl font-bold text-primary">{{ totalWords }}</div>
            <div class="text-sm text-muted-foreground">Total Words</div>
          </div>
          <div class="bg-card p-4 rounded-lg border">
            <div class="text-2xl font-bold text-primary">{{ totalPresentationTime }}s</div>
            <div class="text-sm text-muted-foreground">Total Time</div>
          </div>
          <div class="bg-card p-4 rounded-lg border">
            <div class="text-2xl font-bold text-primary">{{ slidesStore.defaultSlideDuration }}s</div>
            <div class="text-sm text-muted-foreground">Default Duration</div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex flex-wrap gap-4 mb-6">
          <Button @click="exportToPDF" variant="default" :disabled="isExporting" class="bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white">
            <FileText class="w-4 h-4 mr-2" />
            {{ isExporting ? `Exporting... ${exportProgress}%` : 'Export PDF' }}
          </Button>
          <Button @click="exportAllData" variant="outline">
            <Download class="w-4 h-4 mr-2" />
            Export All Data
          </Button>
          <Button @click="toggleEditMode" :variant="isEditMode ? 'default' : 'outline'">
            <Edit class="w-4 h-4 mr-2" />
            {{ isEditMode ? 'Exit Edit Mode' : 'Enter Edit Mode' }}
          </Button>
          <Button @click="saveAllChanges" variant="default" :disabled="!hasChanges">
            <Save class="w-4 h-4 mr-2" />
            Save Changes
          </Button>
          <Button @click="resetAllChanges" variant="destructive" :disabled="!hasChanges">
            <RotateCcw class="w-4 h-4 mr-2" />
            Reset Changes
          </Button>
        </div>

        <!-- PDF Export Progress -->
        <div v-if="isExporting" class="mb-6">
          <div class="bg-card p-4 rounded-lg border">
            <div class="flex items-center space-x-3 mb-2">
              <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              <span class="text-sm font-medium">Generating PDF...</span>
            </div>
            <div class="w-full bg-muted rounded-full h-2">
              <div 
                class="bg-gradient-to-r from-red-600 to-pink-600 h-2 rounded-full transition-all duration-300"
                :style="{ width: `${exportProgress}%` }"
              ></div>
            </div>
            <p class="text-xs text-muted-foreground mt-2">
              Capturing slide {{ Math.ceil((exportProgress / 100) * slidesStore.totalSlides) }} of {{ slidesStore.totalSlides }}
            </p>
          </div>
        </div>
      </div>

      <!-- Tabs Navigation -->
      <div class="mb-8">
        <div class="border-b border-border">
          <nav class="-mb-px flex space-x-8">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              @click="activeTab = tab.id"
              :class="[
                'py-2 px-1 border-b-2 font-medium text-sm transition-colors',
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
              ]"
            >
              <component :is="tab.icon" class="w-4 h-4 mr-2 inline" />
              {{ tab.name }}
              <Badge v-if="tab.count" variant="secondary" class="ml-2">{{ tab.count }}</Badge>
            </button>
          </nav>
        </div>
      </div>

      <!-- Tab Content -->
      <div class="space-y-6">
        <!-- Slide Management Tab -->
        <div v-if="activeTab === 'slides'" class="space-y-6">
          <div class="bg-card p-6 rounded-lg border">
            <div class="flex items-center justify-between mb-6">
              <h2 class="text-2xl font-semibold flex items-center">
                <Clock class="w-6 h-6 mr-2 text-primary" />
                Slide Timing & Management
              </h2>
              <div class="flex items-center space-x-4">
                <div class="flex items-center space-x-2">
                  <label class="text-sm font-medium">Default Duration:</label>
                  <div class="flex items-center space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      @click="updateDefaultDuration(-5)"
                      :disabled="slidesStore.defaultSlideDuration <= 5"
                      class="px-2"
                    >
                      <Minus class="w-3 h-3" />
                    </Button>
                    <span class="bg-muted px-3 py-1 rounded text-sm font-medium min-w-[3rem] text-center">
                      {{ slidesStore.defaultSlideDuration }}s
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      @click="updateDefaultDuration(5)"
                      :disabled="slidesStore.defaultSlideDuration >= 60"
                      class="px-2"
                    >
                      <Plus class="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <Button
                  @click="slidesStore.toggleAutoAdvance()"
                  :variant="slidesStore.isAutoAdvanceEnabled ? 'default' : 'outline'"
                  size="sm"
                >
                  <Play v-if="!slidesStore.isAutoAdvanceEnabled" class="w-4 h-4 mr-1" />
                  <Pause v-else class="w-4 h-4 mr-1" />
                  {{ slidesStore.isAutoAdvanceEnabled ? 'Auto Mode' : 'Manual Mode' }}
                </Button>
              </div>
            </div>

            <div class="grid gap-4">
              <div 
                v-for="(slide, index) in slidesStore.slides" 
                :key="slide.id"
                class="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                :class="{ 'ring-2 ring-primary': index === slidesStore.currentSlideIndex }"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center space-x-4">
                    <div class="flex items-center space-x-2">
                      <span class="text-sm font-medium text-muted-foreground">
                        {{ String(index + 1).padStart(2, '0') }}
                      </span>
                      <div class="w-2 h-2 rounded-full" :class="index === slidesStore.currentSlideIndex ? 'bg-primary' : 'bg-muted-foreground/30'"></div>
                    </div>
                    <div>
                      <h3 class="font-semibold">{{ slide.name }}</h3>
                      <p class="text-sm text-muted-foreground">{{ getSlideFieldCount(slide) }} editable fields</p>
                    </div>
                  </div>
                  
                  <div class="flex items-center space-x-4">
                    <div class="flex items-center space-x-2">
                      <Timer class="w-4 h-4 text-muted-foreground" />
                      <div class="flex items-center space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          @click="updateSlideDuration(slide.id, -5)"
                          :disabled="getSlideDuration(slide) <= 5"
                          class="px-2"
                        >
                          <Minus class="w-3 h-3" />
                        </Button>
                        <span class="bg-muted px-3 py-1 rounded text-sm font-medium min-w-[3rem] text-center">
                          {{ getSlideDuration(slide) }}s
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          @click="updateSlideDuration(slide.id, 5)"
                          :disabled="getSlideDuration(slide) >= 60"
                          class="px-2"
                        >
                          <Plus class="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      @click="slidesStore.goToSlide(index)"
                      class="text-primary hover:text-primary"
                    >
                      <Eye class="w-4 h-4 mr-1" />
                      View
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Content Fields Tab -->
        <div v-if="activeTab === 'content'" class="space-y-6">
          <!-- Titles -->
          <div class="bg-card p-6 rounded-lg border">
            <h3 class="text-xl font-semibold mb-4 flex items-center">
              <Type class="w-5 h-5 mr-2 text-primary" />
              Titles
              <Badge variant="secondary" class="ml-2">{{ titleFields.length }}</Badge>
            </h3>
            <div class="grid gap-4">
              <div 
                v-for="field in titleFields" 
                :key="`${field.slideId}-${field.id}`"
                class="border rounded-lg p-4"
              >
                <div class="flex justify-between items-center mb-3">
                  <span class="text-sm font-medium text-muted-foreground">{{ getSlideNameById(field.slideId) }}</span>
                  <Badge variant="outline">{{ field.type }}</Badge>
                </div>
                <textarea
                  v-model="editableData[`${field.slideId}-${field.id}`]"
                  :readonly="!isEditMode"
                  class="w-full p-3 border rounded-md resize-none text-lg font-semibold transition-colors"
                  :class="{ 
                    'bg-muted cursor-not-allowed': !isEditMode, 
                    'border-primary focus:ring-2 focus:ring-primary/20': isEditMode,
                    'border-orange-500': changedFields.has(`${field.slideId}-${field.id}`)
                  }"
                  rows="2"
                  @input="markAsChanged(`${field.slideId}-${field.id}`)"
                  :placeholder="isEditMode ? 'Enter title...' : ''"
                />
              </div>
            </div>
          </div>

          <!-- Subtitles -->
          <div class="bg-card p-6 rounded-lg border">
            <h3 class="text-xl font-semibold mb-4 flex items-center">
              <Heading class="w-5 h-5 mr-2 text-primary" />
              Subtitles
              <Badge variant="secondary" class="ml-2">{{ subtitleFields.length }}</Badge>
            </h3>
            <div class="grid gap-4">
              <div 
                v-for="field in subtitleFields" 
                :key="`${field.slideId}-${field.id}`"
                class="border rounded-lg p-4"
              >
                <div class="flex justify-between items-center mb-3">
                  <span class="text-sm font-medium text-muted-foreground">{{ getSlideNameById(field.slideId) }}</span>
                  <Badge variant="outline">{{ field.type }}</Badge>
                </div>
                <textarea
                  v-model="editableData[`${field.slideId}-${field.id}`]"
                  :readonly="!isEditMode"
                  class="w-full p-3 border rounded-md resize-none transition-colors"
                  :class="{ 
                    'bg-muted cursor-not-allowed': !isEditMode, 
                    'border-primary focus:ring-2 focus:ring-primary/20': isEditMode,
                    'border-orange-500': changedFields.has(`${field.slideId}-${field.id}`)
                  }"
                  rows="2"
                  @input="markAsChanged(`${field.slideId}-${field.id}`)"
                  :placeholder="isEditMode ? 'Enter subtitle...' : ''"
                />
              </div>
            </div>
          </div>

          <!-- Content -->
          <div class="bg-card p-6 rounded-lg border">
            <h3 class="text-xl font-semibold mb-4 flex items-center">
              <FileText class="w-5 h-5 mr-2 text-primary" />
              Content
              <Badge variant="secondary" class="ml-2">{{ contentFields.length }}</Badge>
            </h3>
            <div class="grid gap-4">
              <div 
                v-for="field in contentFields" 
                :key="`${field.slideId}-${field.id}`"
                class="border rounded-lg p-4"
              >
                <div class="flex justify-between items-center mb-3">
                  <span class="text-sm font-medium text-muted-foreground">{{ getSlideNameById(field.slideId) }}</span>
                  <Badge variant="outline">{{ field.type }}</Badge>
                </div>
                <textarea
                  v-model="editableData[`${field.slideId}-${field.id}`]"
                  :readonly="!isEditMode"
                  class="w-full p-3 border rounded-md resize-none transition-colors"
                  :class="{ 
                    'bg-muted cursor-not-allowed': !isEditMode, 
                    'border-primary focus:ring-2 focus:ring-primary/20': isEditMode,
                    'border-orange-500': changedFields.has(`${field.slideId}-${field.id}`)
                  }"
                  rows="4"
                  @input="markAsChanged(`${field.slideId}-${field.id}`)"
                  :placeholder="isEditMode ? 'Enter content...' : ''"
                />
              </div>
            </div>
          </div>

          <!-- Bullet Points -->
          <div class="bg-card p-6 rounded-lg border">
            <h3 class="text-xl font-semibold mb-4 flex items-center">
              <List class="w-5 h-5 mr-2 text-primary" />
              Bullet Points
              <Badge variant="secondary" class="ml-2">{{ bulletPointFields.length }}</Badge>
            </h3>
            <div class="grid gap-4">
              <div 
                v-for="field in bulletPointFields" 
                :key="`${field.slideId}-${field.id}`"
                class="border rounded-lg p-4"
              >
                <div class="flex justify-between items-center mb-3">
                  <span class="text-sm font-medium text-muted-foreground">{{ getSlideNameById(field.slideId) }}</span>
                  <Badge variant="outline">{{ field.type }}</Badge>
                </div>
                <div class="space-y-3">
                  <div 
                    v-for="(point, index) in getBulletPoints(`${field.slideId}-${field.id}`)" 
                    :key="index"
                    class="flex items-start space-x-3"
                  >
                    <div class="flex items-center justify-center w-6 h-6 rounded-full bg-muted text-xs font-medium mt-2">
                      {{ index + 1 }}
                    </div>
                    <textarea
                      v-model="editableData[`${field.slideId}-${field.id}`][index]"
                      :readonly="!isEditMode"
                      class="flex-1 p-3 border rounded-md resize-none transition-colors"
                      :class="{ 
                        'bg-muted cursor-not-allowed': !isEditMode, 
                        'border-primary focus:ring-2 focus:ring-primary/20': isEditMode,
                        'border-orange-500': changedFields.has(`${field.slideId}-${field.id}`)
                      }"
                      rows="2"
                      @input="markAsChanged(`${field.slideId}-${field.id}`)"
                      :placeholder="isEditMode ? `Enter bullet point ${index + 1}...` : ''"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Analytics Tab -->
        <div v-if="activeTab === 'analytics'" class="space-y-6">
          <div class="bg-card p-6 rounded-lg border">
            <h3 class="text-xl font-semibold mb-4 flex items-center">
              <BarChart class="w-5 h-5 mr-2 text-primary" />
              Content Analytics
            </h3>
            <div class="grid md:grid-cols-2 gap-6">
              <div>
                <h4 class="font-medium mb-3">Content Distribution</h4>
                <div class="space-y-2">
                  <div class="flex justify-between items-center">
                    <span class="text-sm">Titles</span>
                    <span class="text-sm font-medium">{{ titleFields.length }}</span>
                  </div>
                  <div class="flex justify-between items-center">
                    <span class="text-sm">Subtitles</span>
                    <span class="text-sm font-medium">{{ subtitleFields.length }}</span>
                  </div>
                  <div class="flex justify-between items-center">
                    <span class="text-sm">Content Blocks</span>
                    <span class="text-sm font-medium">{{ contentFields.length }}</span>
                  </div>
                  <div class="flex justify-between items-center">
                    <span class="text-sm">Bullet Point Lists</span>
                    <span class="text-sm font-medium">{{ bulletPointFields.length }}</span>
                  </div>
                </div>
              </div>
              <div>
                <h4 class="font-medium mb-3">Timing Analysis</h4>
                <div class="space-y-2">
                  <div class="flex justify-between items-center">
                    <span class="text-sm">Total Presentation Time</span>
                    <span class="text-sm font-medium">{{ Math.floor(totalPresentationTime / 60) }}m {{ totalPresentationTime % 60 }}s</span>
                  </div>
                  <div class="flex justify-between items-center">
                    <span class="text-sm">Average Slide Duration</span>
                    <span class="text-sm font-medium">{{ Math.round(totalPresentationTime / slidesStore.totalSlides) }}s</span>
                  </div>
                  <div class="flex justify-between items-center">
                    <span class="text-sm">Shortest Slide</span>
                    <span class="text-sm font-medium">{{ shortestSlideDuration }}s</span>
                  </div>
                  <div class="flex justify-between items-center">
                    <span class="text-sm">Longest Slide</span>
                    <span class="text-sm font-medium">{{ longestSlideDuration }}s</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Back to Presentation -->
      <div class="mt-12 text-center">
        <Button @click="$router.push('/')" size="lg" variant="outline">
          <ArrowLeft class="w-4 h-4 mr-2" />
          Back to Presentation
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, reactive, onMounted } from 'vue'
import { useSlidesStore } from '~/stores/slides'
import { usePDFExport } from '~/composables/usePDFExport'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { 
  Download, 
  Edit, 
  Save, 
  RotateCcw, 
  Type, 
  Heading, 
  FileText, 
  List, 
  ArrowLeft,
  Clock,
  Timer,
  Play,
  Pause,
  Plus,
  Minus,
  Eye,
  BarChart,
  Presentation
} from 'lucide-vue-next'

// Set page head
useHead({
  title: 'AI Data Overview - BRX.ai',
  meta: [
    { name: 'description', content: 'AI-powered data overview and editing interface for BRX.ai pitch deck' }
  ]
})

const slidesStore = useSlidesStore()
const { exportToPDF, isExporting, exportProgress } = usePDFExport()
const isEditMode = ref(false)
const hasChanges = ref(false)
const changedFields = ref(new Set<string>())
const activeTab = ref('slides')

// Tab configuration
const tabs = computed(() => [
  {
    id: 'slides',
    name: 'Slide Management',
    icon: Presentation,
    count: slidesStore.totalSlides
  },
  {
    id: 'content',
    name: 'Content Fields',
    icon: Edit,
    count: totalEditableFields.value
  },
  {
    id: 'analytics',
    name: 'Analytics',
    icon: BarChart,
    count: null
  }
])

// Reactive data for editing
const editableData = reactive<Record<string, any>>({})

// Initialize editable data
const initializeEditableData = () => {
  slidesStore.slides.forEach(slide => {
    slide.fields.forEach(field => {
      if (field.editable) {
        const key = `${slide.id}-${field.id}`
        editableData[key] = Array.isArray(field.value) 
          ? [...field.value] 
          : field.value
      }
    })
  })
}

// Get slide name by ID
const getSlideNameById = (slideId: string) => {
  const slide = slidesStore.slides.find(s => s.id === slideId)
  return slide?.name || slideId
}

// Get slide field count
const getSlideFieldCount = (slide: any) => {
  return slide.fields.filter((field: any) => field.editable).length
}

// Get slide duration
const getSlideDuration = (slide: any) => {
  return slide.duration || slidesStore.defaultSlideDuration
}

// Update slide duration
const updateSlideDuration = (slideId: string, delta: number) => {
  const slide = slidesStore.slides.find(s => s.id === slideId)
  if (slide) {
    const currentDuration = slide.duration || slidesStore.defaultSlideDuration
    const newDuration = Math.max(5, Math.min(60, currentDuration + delta))
    slidesStore.updateSlideDuration(slideId, newDuration)
  }
}

// Update default duration
const updateDefaultDuration = (delta: number) => {
  const newDuration = Math.max(5, Math.min(60, slidesStore.defaultSlideDuration + delta))
  slidesStore.setDefaultDuration(newDuration)
}

// Get fields by type with slide info
const getFieldsByType = (type: string) => {
  const fields: Array<{ slideId: string; id: string; type: string; value: any }> = []
  slidesStore.slides.forEach(slide => {
    slide.fields.forEach(field => {
      if (field.type === type && field.editable) {
        fields.push({
          slideId: slide.id,
          id: field.id,
          type: field.type,
          value: field.value
        })
      }
    })
  })
  return fields
}

// Computed properties for different field types
const titleFields = computed(() => getFieldsByType('title'))
const subtitleFields = computed(() => getFieldsByType('subtitle'))
const contentFields = computed(() => getFieldsByType('content'))
const bulletPointFields = computed(() => getFieldsByType('bulletPoints'))

// Get bullet points for a field
const getBulletPoints = (key: string) => {
  return Array.isArray(editableData[key]) ? editableData[key] : []
}

// Statistics
const totalEditableFields = computed(() => {
  return slidesStore.getAllEditableFields.length
})

const totalWords = computed(() => {
  let wordCount = 0
  Object.values(editableData).forEach(value => {
    if (typeof value === 'string') {
      wordCount += value.split(/\s+/).filter(word => word.length > 0).length
    } else if (Array.isArray(value)) {
      value.forEach(item => {
        if (typeof item === 'string') {
          wordCount += item.split(/\s+/).filter(word => word.length > 0).length
        }
      })
    }
  })
  return wordCount
})

const totalPresentationTime = computed(() => {
  return slidesStore.slides.reduce((total, slide) => {
    return total + (slide.duration || slidesStore.defaultSlideDuration)
  }, 0)
})

const shortestSlideDuration = computed(() => {
  const durations = slidesStore.slides.map(slide => slide.duration || slidesStore.defaultSlideDuration)
  return Math.min(...durations)
})

const longestSlideDuration = computed(() => {
  const durations = slidesStore.slides.map(slide => slide.duration || slidesStore.defaultSlideDuration)
  return Math.max(...durations)
})

// Actions
const toggleEditMode = () => {
  isEditMode.value = !isEditMode.value
}

const markAsChanged = (fieldKey: string) => {
  changedFields.value.add(fieldKey)
  hasChanges.value = true
}

const saveAllChanges = () => {
  // Apply changes to the store
  changedFields.value.forEach(fieldKey => {
    const [slideId, fieldId] = fieldKey.split('-')
    const value = editableData[fieldKey]
    slidesStore.updateField(slideId, fieldId, value)
  })
  
  // Clear changes
  changedFields.value.clear()
  hasChanges.value = false
  
  // Show success message (you could add a toast here)
  console.log('All changes saved successfully!')
}

const resetAllChanges = () => {
  // Reinitialize data from store
  initializeEditableData()
  changedFields.value.clear()
  hasChanges.value = false
}

const exportAllData = () => {
  const exportData = {
    slides: slidesStore.slides,
    settings: {
      defaultSlideDuration: slidesStore.defaultSlideDuration,
      isAutoAdvanceEnabled: slidesStore.isAutoAdvanceEnabled,
      currentColorPalette: slidesStore.currentColorPalette
    },
    statistics: {
      totalSlides: slidesStore.totalSlides,
      totalEditableFields: totalEditableFields.value,
      totalWords: totalWords.value,
      totalPresentationTime: totalPresentationTime.value,
      averageSlideDuration: Math.round(totalPresentationTime.value / slidesStore.totalSlides)
    },
    exportedAt: new Date().toISOString()
  }
  
  const blob = new Blob([JSON.stringify(exportData, null, 2)], { 
    type: 'application/json' 
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `brx-ai-pitch-deck-data-${new Date().toISOString().split('T')[0]}.json`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// Initialize on mount
onMounted(() => {
  initializeEditableData()
})
</script>

<style scoped>
.ai-overview-page {
  width: 100%;
  padding-top: 60px; /* Account for fixed navbar */
  overflow-x: hidden;
  overflow-y: auto;
  /* Hide scrollbar for webkit browsers */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
}

/* Additional scrollbar hiding for different browsers */
.ai-overview-page {
  -webkit-overflow-scrolling: touch;
}

.ai-overview-page::-webkit-scrollbar-track {
  display: none;
}

.ai-overview-page::-webkit-scrollbar-thumb {
  display: none;
}

.ai-overview-page::-webkit-scrollbar {
  display: none; /* Safari and Chrome */
  width: 0;
  background: transparent;
}

/* Additional scrollbar hiding for different browsers */
.ai-overview-page {
  -webkit-overflow-scrolling: touch;
}

.ai-overview-page::-webkit-scrollbar-track {
  display: none;
}

.ai-overview-page::-webkit-scrollbar-thumb {
  display: none;
}

textarea:focus {
  outline: none;
}

.border-primary {
  border-color: hsl(var(--primary));
}

/* Smooth transitions */
.transition-colors {
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

/* Tab styling */
nav button {
  transition: all 0.2s ease;
}

/* Changed field indicator */
.border-orange-500 {
  border-color: rgb(249 115 22);
  box-shadow: 0 0 0 1px rgb(249 115 22 / 0.2);
}

/* Focus ring for better accessibility */
textarea:focus {
  ring: 2px;
  ring-color: hsl(var(--primary));
  ring-offset: 2px;
}

/* Hover effects */
.hover\:bg-muted\/50:hover {
  background-color: hsl(var(--muted) / 0.5);
}

/* Tab active state */
.border-primary {
  border-color: hsl(var(--primary));
}

/* Card hover effects */
.bg-card {
  background-color: hsl(var(--card));
}

/* Gradient text */
.bg-gradient-to-r {
  background-image: linear-gradient(to right, var(--tw-gradient-stops));
}

.from-blue-600 {
  --tw-gradient-from: rgb(37 99 235);
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgb(37 99 235 / 0));
}

.to-purple-600 {
  --tw-gradient-to: rgb(147 51 234);
}

.bg-clip-text {
  background-clip: text;
  -webkit-background-clip: text;
}

.text-transparent {
  color: transparent;
}

/* Ring styles for current slide */
.ring-2 {
  box-shadow: 0 0 0 2px hsl(var(--primary));
}

.ring-primary {
  --tw-ring-color: hsl(var(--primary));
}

/* Smooth animations */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Badge styles */
.badge {
  display: inline-flex;
  align-items: center;
  border-radius: calc(var(--radius) - 2px);
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  transition: colors 0.2s;
}

/* Button hover states */
button:hover {
  transition: all 0.2s ease;
}

/* Disabled states */
button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Responsive grid adjustments */
@media (max-width: 768px) {
  .grid-cols-5 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  
  .md\:grid-cols-2 {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
}

/* Custom scrollbar for textareas */
textarea::-webkit-scrollbar {
  width: 4px;
}

textarea::-webkit-scrollbar-track {
  background: hsl(var(--muted));
}

textarea::-webkit-scrollbar-thumb {
  background: hsl(var(--muted-foreground));
  border-radius: 2px;
}

textarea::-webkit-scrollbar-thumb:hover {
  background: hsl(var(--primary));
}
</style>
